import { IDateRangeFilter } from 'submodules/nerit-framework-ui/common/components/form-fields/date-range-picker/inner/IDateRangeFilter'
import { SelectFullOptionTP } from 'submodules/nerit-framework-ui/common/components/form-fields/select/inner/types/SelectOptionTP'

/** Um mes dentro do intervalo da grade (Tela 3.2). */
export type MonthColumnTP = {
    year: number,
    month: number,

    /** Chave estavel usada como dataIndex da coluna (ex.: '2024_3'). */
    key: string,

    /** Rotulo curto exibido no cabecalho (ex.: 'Mar/24'). */
    label: string,
}

/** Intervalo de meses fechados normalizado, no formato esperado pelo backend. */
export type MonthRangeTP = {
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number,
}

const MONTH_ABBREVIATIONS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

/**
 * Utilitarios para lidar com o filtro de periodo (intervalo de meses fechados).
 */
export class MonthRangeUtils {

    /**
     * Rotulo curto de um mes/ano (ex.: 'Mar/24').
     */
    static getMonthLabel(year: number, month: number): string {
        const abbreviation = MONTH_ABBREVIATIONS[month - 1] ?? String(month)
        return `${abbreviation}/${String(year).slice(-2)}`
    }

    /**
     * Gera a lista ordenada de meses entre inicio e fim (inclusivos).
     */
    static buildMonths(range: MonthRangeTP): MonthColumnTP[] {
        const months: MonthColumnTP[] = []

        let year = range.startYear
        let month = range.startMonth

        // Protege contra intervalos invertidos: sem meses.
        while (year < range.endYear || (year === range.endYear && month <= range.endMonth)) {
            months.push({
                year,
                month,
                key: `${year}_${month}`,
                label: MonthRangeUtils.getMonthLabel(year, month),
            })

            month++
            if (month > 12) {
                month = 1
                year++
            }
        }

        return months
    }

    /**
     * Rotulo do mes por extenso (ex.: 'Março de 2025').
     */
    static getFullMonthLabel(year: number, month: number): string {
        const name = MONTH_NAMES[month - 1] ?? String(month)
        return `${name} de ${year}`
    }

    /**
     * Opcoes (mes 1-12) para um select de mes unico (Tela 3.3).
     */
    static getMonthSelectOptions(): SelectFullOptionTP[] {
        return MONTH_NAMES.map((name, index) => ({
            value: index + 1,
            label: name,
        }))
    }

    /**
     * Opcoes de ano para um select, do ano atual ate `yearsBack` anos atras.
     */
    static getYearSelectOptions(currentYear: number, yearsBack = 10): SelectFullOptionTP[] {
        const options: SelectFullOptionTP[] = []
        for (let year = currentYear; year >= currentYear - yearsBack; year--)
            options.push({ value: year, label: String(year) })
        return options
    }

    /**
     * Converte o valor do DateRangePickerCP no intervalo normalizado, extraindo
     * apenas mes e ano de cada data (o dia e ignorado). Retorna undefined quando
     * o intervalo esta incompleto.
     */
    static fromDateRange(dateRange?: IDateRangeFilter): MonthRangeTP | undefined {
        if (!dateRange?.beginDate || !dateRange?.endDate)
            return undefined

        const begin = new Date(dateRange.beginDate)
        const end = new Date(dateRange.endDate)

        return {
            startYear: begin.getFullYear(),
            startMonth: begin.getMonth() + 1,
            endYear: end.getFullYear(),
            endMonth: end.getMonth() + 1,
        }
    }

}
